import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Slider,
} from '@mui/material';
import PageHeader from '../components/PageHeader';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import apiClient from '../services/apiClient';
import transcriptionService from '../services/transcriptionService';
import type { SessionSummary, AudioRecordingItem } from '../services/transcriptionService';
import { format } from 'date-fns';
import { typography } from '~/theme';

const SessionDetail: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<SessionSummary | null>(null);
  const [recordings, setRecordings] = useState<AudioRecordingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Audio playback state (for multiple recordings)
  const audioRefs = useRef<Record<number, HTMLAudioElement | null>>({});
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [currentTimes, setCurrentTimes] = useState<Record<number, number>>({});
  const [durations, setDurations] = useState<Record<number, number>>({});
  const [audioBlobUrls, setAudioBlobUrls] = useState<Record<number, string>>({});
  const [audioLoading, setAudioLoading] = useState<Record<number, boolean>>({});
  const [audioErrors, setAudioErrors] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!sessionId) return;
    setLoading(true);
    Promise.all([
      transcriptionService.getSessionSummary(sessionId),
      transcriptionService.getSessionRecordings(sessionId),
    ])
      .then(([sessionData, recordingsData]) => {
        setSession(sessionData);
        setRecordings(recordingsData);
        setError(null);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || 'Failed to load session');
      })
      .finally(() => setLoading(false));
  }, [sessionId]);

  // Fetch all recordings audio via apiClient (which injects the JWT token) and create blob URLs.
  // Native <audio src="..."> cannot send Authorization headers.
  useEffect(() => {
    if (!sessionId || recordings.length === 0) return;

    const loadRecordings = async () => {
      const newBlobUrls: Record<number, string> = {};
      const newLoading: Record<number, boolean> = {};
      const newErrors: Record<number, string> = {};

      for (const recording of recordings) {
        if (recording.status !== 'completed') continue;

        newLoading[recording.id] = true;
        const url = transcriptionService.getRecordingAudioUrl(sessionId, recording.id);

        try {
          const resp = await apiClient.get(url, { responseType: 'blob' });
          const blob = new Blob([resp.data], { type: 'audio/wav' });
          newBlobUrls[recording.id] = URL.createObjectURL(blob);
        } catch (err) {
          console.error('Failed to load audio recording:', recording.id, err);
          newErrors[recording.id] = 'Failed to load audio';
        } finally {
          newLoading[recording.id] = false;
        }
      }

      setAudioBlobUrls(newBlobUrls);
      setAudioLoading(newLoading);
      setAudioErrors(newErrors);
    };

    loadRecordings();

    return () => {
      Object.values(audioBlobUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordings, sessionId]);

  // Setup audio event listeners for each loaded recording
  useEffect(() => {
    const handlers: Record<number, { onTimeUpdate: () => void; onLoadedMetadata: () => void; onEnded: () => void }> = {};

    Object.entries(audioRefs.current).forEach(([idStr, audio]) => {
      if (!audio) return;
      const id = Number(idStr);

      const onTimeUpdate = () => setCurrentTimes((prev) => ({ ...prev, [id]: audio.currentTime }));
      const onLoadedMetadata = () => setDurations((prev) => ({ ...prev, [id]: audio.duration }));
      const onEnded = () => setPlayingId((prev) => (prev === id ? null : prev));

      handlers[id] = { onTimeUpdate, onLoadedMetadata, onEnded };

      audio.addEventListener('timeupdate', onTimeUpdate);
      audio.addEventListener('loadedmetadata', onLoadedMetadata);
      audio.addEventListener('ended', onEnded);
    });

    return () => {
      Object.entries(handlers).forEach(([idStr, h]) => {
        const id = Number(idStr);
        const audio = audioRefs.current[id];
        if (!audio) return;
        audio.removeEventListener('timeupdate', h.onTimeUpdate);
        audio.removeEventListener('loadedmetadata', h.onLoadedMetadata);
        audio.removeEventListener('ended', h.onEnded);
      });
    };
  }, [audioBlobUrls]);

  const handlePlayPause = (id: number) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    // Stop any other playing audio
    if (playingId !== null && playingId !== id) {
      const otherAudio = audioRefs.current[playingId];
      if (otherAudio) {
        otherAudio.pause();
      }
    }

    if (playingId === id) {
      audio.pause();
      setPlayingId(null);
    } else {
      audio.play();
      setPlayingId(id);
    }
  };

  const handleStop = (id: number) => {
    const audio = audioRefs.current[id];
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setCurrentTimes((prev) => ({ ...prev, [id]: 0 }));
    if (playingId === id) setPlayingId(null);
  };

  const handleSeek = (id: number, value: number) => {
    const audio = audioRefs.current[id];
    if (!audio) return;
    audio.currentTime = value;
    setCurrentTimes((prev) => ({ ...prev, [id]: value }));
  };

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !session) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader
          title="Session Details"
          onBack={() => navigate('/transcriptions')}
          backLabel="Back to Dictation History"
        />
        <Typography color="error">{error || 'Session not found'}</Typography>
      </Box>
    );
  }

  const hasAudio = recordings.length > 0 || (session.audio && session.audio.status === 'completed');
  const allRecordings = recordings.length > 0 ? recordings : (session.audio ? [session.audio as unknown as AudioRecordingItem] : []);

  return (
    <Box sx={{ height: '100vh', overflowY: 'auto', p: 2, pb: 8 }}>
      <PageHeader
        title="Session Details"
        onBack={() => navigate('/transcriptions')}
        backLabel="Back to Dictation History"
      />

      {/* Session metadata */}
      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Session ID</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {session.session_id}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Created</Typography>
            <Typography variant="body2">{formatDate(session.created_at)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Duration</Typography>
            <Typography variant="body2">{formatTime(session.duration_seconds)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Chip
              label={session.status}
              color={session.status === 'completed' ? 'success' : 'primary'}
              size="small"
              sx={{ height: 22, textTransform: 'capitalize', fontWeight: typography.button.fontWeight, alignSelf: 'flex-start' }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Audio player(s) */}
      {hasAudio && (
        <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Audio {allRecordings.length > 1 ? `Recordings (${allRecordings.length})` : 'Recording'}
          </Typography>

          {allRecordings.map((rec) => (
            <Box key={rec.id} sx={{ mb: 2 }}>
              {allRecordings.length > 1 && (
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Recording #{rec.id} — {formatTime(rec.duration_seconds)} · {formatFileSize(rec.file_size_bytes)}
                </Typography>
              )}

              {audioLoading[rec.id] && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">Loading audio...</Typography>
                </Box>
              )}
              {audioErrors[rec.id] && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>{audioErrors[rec.id]}</Typography>
              )}
              {audioBlobUrls[rec.id] && (
                <audio
                  ref={(el) => { audioRefs.current[rec.id] = el; }}
                  src={audioBlobUrls[rec.id]}
                  preload="metadata"
                />
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <IconButton
                  onClick={() => handlePlayPause(rec.id)}
                  color="primary"
                  size="large"
                >
                  {playingId === rec.id ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
                <IconButton onClick={() => handleStop(rec.id)} size="small">
                  <StopIcon />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 50 }}>
                  {formatTime(currentTimes[rec.id] || 0)}
                </Typography>
                <Slider
                  value={currentTimes[rec.id] || 0}
                  max={durations[rec.id] || rec.duration_seconds || 1}
                  onChange={(_, value) => handleSeek(rec.id, value as number)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <Typography variant="body2" sx={{ minWidth: 50 }}>
                  {formatTime(durations[rec.id] || rec.duration_seconds)}
                </Typography>
              </Box>

              {allRecordings.length === 1 && (
                <Typography variant="caption" color="text.secondary">
                  {rec.sample_rate} Hz · {formatFileSize(rec.file_size_bytes)}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}

      {/* Transcript */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Transcript</Typography>
        <Divider sx={{ mb: 2 }} />

        {session.corrected_text && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Corrected
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f0fdf4' }}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {session.corrected_text}
              </Typography>
            </Paper>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {session.corrected_text ? 'Original' : 'Transcription'}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: '50vh', overflow: 'auto' }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {session.text || 'No transcription text available.'}
            </Typography>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default SessionDetail;
